/* eslint-disable no-console */

/**
 * Device delete = lockout (user decision 2026-09-19). On an NC1 account the
 * owner-signed device list is brought in step with the Devices page by the
 * owner's unlocked browser - there is no separate "a monitored device was
 * lost" step. DeviceRetirement.js has the rules; this is the glue:
 *
 *   retireDeletedDevices  right after Devices > Delete succeeded - tombstones
 *                         exactly the devices that were deleted;
 *   sweepDeviceList       the catch-up, run when the dashboard loads and again
 *                         after the owner unlocks - covers a delete this
 *                         browser could not sign at the time (it was locked,
 *                         an admin's browser did it, the app was uninstalled)
 *                         and drops tombstones past their retention.
 *
 * The sweep decides from the account's registered devices, and the store's
 * copy of the account is persisted across sessions (it can be days old), so
 * it always fetches the account fresh and gives up rather than guess when
 * anything about the answer looks off - a wrong tombstone takes that install
 * off encryption until it is registered again.
 *
 * Idempotent and serialized per owner - ONE device-list writer at a time:
 * the uploader approval in DeviceLink.js joins the same queue through
 * inDeviceListQueue. Never throws.
 */
import E2eeApi from '@/aws/e2ee/E2eeApi'
import api from '@/lib/api'
import E2EEPasscodePrompt from '../E2EEPasscodePrompt'
import { E2EE_ERROR_CODES, classifyE2EEError } from '../E2EEErrors'
import { hasCachedIdentity } from './Unlock'
import { retireDevices } from './Rotate'
import { planDeviceRetirement, deviceUuidFromLegacyId } from './DeviceRetirement'

const queue = new Map()         // ownerEmail -> tail of that owner's promise chain
const pendingDeletes = new Map() // ownerEmail -> Set<deviceUuid> this browser could not sign yet
const sweepWanted = new Set()    // ownerEmails to re-sweep after an unlock

const norm = (e) => String(e || '').toLowerCase()

function serialized (ownerEmail, job) {
  const run = (queue.get(ownerEmail) || Promise.resolve()).then(job, job)
  queue.set(ownerEmail, run.catch(() => {}))
  return run
}

/**
 * Run `job` as the owner's next device-list writer. Every signed re-signing
 * of the list this browser makes goes through one queue per owner - the
 * sweep and the delete tombstone here, the uploader approval in
 * DeviceLink.js - so no two of them build on the same version. They used
 * to: the inline unlock in the dashboard's "Add device request" dialog
 * starts the deferred tombstone of a device deleted while the browser was
 * locked, and the same unlock enables Accept, so the approval raced that
 * re-signing and lost by a third of a second ("device-list version must be
 * N (got N-1)", dev 2026-10-13). The job's own result and error are the
 * caller's; a failed job never blocks the next one.
 */
export function inDeviceListQueue (ownerEmail, job) {
  return serialized(norm(ownerEmail), job)
}

function note (result) {
  if (result.tombstoned.length || result.pruned.length) {
    console.log(`nc1 device list: ${result.tombstoned.length} deleted device(s) retired, `
      + `${result.pruned.length} old entr(ies) dropped (v${result.listVersion})`)
  }
}

/**
 * The devices with these LEGACY ids were just deleted: tombstone them on the
 * signed list. @returns {Promise<{tombstoned: string[], pruned: string[], unlockRequired: boolean, error?: string}>}
 */
export function retireDeletedDevices (ownerEmail, legacyDeviceIds) {
  ownerEmail = norm(ownerEmail)
  const uuids = (legacyDeviceIds || []).map(deviceUuidFromLegacyId).filter(Boolean)
  return serialized(ownerEmail, async () => {
    const result = { tombstoned: [], pruned: [], unlockRequired: false }
    try {
      if (!E2eeApi.isConfigured() || uuids.length === 0) return result
      const waiting = pendingDeletes.get(ownerEmail) || new Set()
      for (const u of uuids) waiting.add(u)
      pendingDeletes.set(ownerEmail, waiting)
      if (!(await hasCachedIdentity(ownerEmail))) {
        // Finished after the unlock (hook below), or by any later sweep.
        result.unlockRequired = true
        return result
      }
      Object.assign(result, await retireDevices({ email: ownerEmail, deletedDeviceIds: [...waiting] }))
      pendingDeletes.delete(ownerEmail)
      note(result)
    } catch (e) {
      if (classifyE2EEError(e).code === E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED) result.unlockRequired = true
      else {
        console.log('nc1 device list: retiring deleted devices failed', e)
        result.error = (e && e.message) || String(e)
      }
    }
    return result
  })
}

/** The catch-up sweep. @returns the same shape as retireDeletedDevices. */
export function sweepDeviceList (ownerEmail) {
  ownerEmail = norm(ownerEmail)
  return serialized(ownerEmail, async () => {
    const result = { tombstoned: [], pruned: [], unlockRequired: false }
    try {
      if (!E2eeApi.isConfigured() || !ownerEmail) return result
      if (!(await hasCachedIdentity(ownerEmail))) {
        sweepWanted.add(ownerEmail)
        result.unlockRequired = true
        return result
      }
      const list = (await E2eeApi.getDeviceList(ownerEmail)).data
      if (!list || !Array.isArray(list.devices) || list.devices.length === 0) return result

      // The registered devices, fetched NOW. `identities` comes off the
      // account row in one read, so it is complete (the devices endpoint is a
      // batch read that may come back partial).
      const resp = await api.getAccountAwait(false)
      const data = resp && resp.status === 200 && resp.data
      const identities = data && data.account && data.account.identities
      if (!data || norm(data.accountId) !== ownerEmail || !Array.isArray(identities) || identities.length === 0) {
        console.log('nc1 device list: sweep skipped, the account could not be read')
        return result
      }
      const registered = []
      for (const ident of identities) {
        const id = ident && ident.id
        // A login that never sent a device UUID has a bare id (no '|'); it can
        // never be on the signed list, so it is simply not a candidate.
        if (typeof id === 'string' && id && !id.includes('|')) continue
        const uuid = deviceUuidFromLegacyId(id)
        if (!uuid) {
          // An id this code cannot read means the set may be incomplete: never guess.
          console.log('nc1 device list: sweep skipped, a device id was not understood')
          return result
        }
        registered.push(uuid)
      }

      const deleted = [...(pendingDeletes.get(ownerEmail) || [])]
      const plan = planDeviceRetirement(list, { deletedDeviceIds: deleted, registeredDeviceIds: registered })
      if (!plan.tombstone.length && !plan.prune.length) return result
      Object.assign(result, await retireDevices({
        email: ownerEmail, deletedDeviceIds: deleted, registeredDeviceIds: registered,
      }))
      pendingDeletes.delete(ownerEmail)
      sweepWanted.delete(ownerEmail)
      note(result)
    } catch (e) {
      if (classifyE2EEError(e).code === E2EE_ERROR_CODES.NC1_UNLOCK_REQUIRED) {
        sweepWanted.add(ownerEmail)
        result.unlockRequired = true
      } else {
        console.log('nc1 device list: sweep failed', e)
        result.error = (e && e.message) || String(e)
      }
    }
    return result
  })
}

// After the owner unlocks this browser, finish what could not be signed
// before. The unlock channel is shared with the legacy passcode prompt; only
// an owner this module was asked about re-runs.
E2EEPasscodePrompt.onUnlock((accountId) => {
  const ownerEmail = norm(accountId)
  if (sweepWanted.has(ownerEmail) || pendingDeletes.has(ownerEmail)) sweepDeviceList(ownerEmail).catch(() => {})
})
